import { useState } from 'react';
import type { BucketKey, CostPerPersonRow } from '@fleet/shared';
import { BUCKETS, euro } from '@fleet/shared';
import { InfoTip } from '../../components/ui/InfoTip';
import { PeriodeKiezer } from '../../components/ui/PeriodeKiezer';
import { MeerTonen } from '../../components/ui/MeerTonen';
import { Panel } from '../../components/ui/Panel';
import { PersoonLink } from '../../components/ui/EntiteitLink';

const COLOR = Object.fromEntries(BUCKETS.map((b) => [b.key, b.color])) as Record<BucketKey, string>;
const LABEL = Object.fromEntries(BUCKETS.map((b) => [b.key, b.label])) as Record<BucketKey, string>;

const CAP = 8;

/**
 * Who spends the variable costs.
 *
 * The one dimension the rest of the page does not have: cars are ranked in
 * *Duurste auto's*, categories are the legend of the monthly chart, months are
 * that chart's axis. This block used to carry all three as well, under the
 * heading "Kosten uitgesplitst" — which promised every cost while showing only
 * the measured half, and repeated two panels that sit directly above it.
 *
 * The unattributable transactions are not here at all. They were the largest bar,
 * at nearly twice the biggest real person, so everyone else was measured against
 * a stub belonging to nobody. Three quarters of them are maintenance invoices,
 * which carry no fuel card and never will — those belong to a car. What is left
 * is fixable and sits in the action centre as *Kosten zonder persoon*, where a
 * missing coupling can actually be added.
 */
export function KostenPerPersoon({
  rijen,
  periodLabel,
  months,
  period,
  onPeriod,
  onSelectPerson,
}: {
  rijen: CostPerPersonRow[];
  periodLabel: string;
  months: string[];
  period: string;
  onPeriod: (p: string | null) => void;
  onSelectPerson: (persoonId: string) => void;
}) {
  const [uit, setUit] = useState<Partial<Record<BucketKey, boolean>>>({});
  const aan = (k: BucketKey) => !uit[k];

  // Categorieën die in deze periode überhaupt voorkomen, met hun totaal: dat is
  // de legenda én het filter, net als bij de grafieken erboven.
  const totalen = new Map<BucketKey, number>();
  for (const r of rijen) {
    if (r.persoonId == null) continue;
    for (const s of r.segments) totalen.set(s.key, (totalen.get(s.key) ?? 0) + s.value);
  }
  const lagen = BUCKETS.filter((b) => (totalen.get(b.key) ?? 0) > 0);

  // Filteren verandert de vraag: met alleen Laden aan lees je wie het meest
  // laadt, en dan hoort die ook bovenaan te staan. Dus opnieuw sorteren en
  // opnieuw schalen in plaats van de oude volgorde met gaten erin.
  const personen = rijen
    .filter((r) => r.persoonId != null)
    .map((r) => {
      const segments = r.segments.filter((s) => aan(s.key));
      return { ...r, segments, bedrag: segments.reduce((s, x) => s + x.value, 0) };
    })
    .filter((r) => r.bedrag > 0)
    .sort((a, b) => b.bedrag - a.bedrag);

  // Geschaald op de grootste persoon, niet op het totaal: anders is de langste
  // balk halverwege en staat de rest als streepje.
  const max = Math.max(1, ...personen.map((r) => r.bedrag));

  return (
    <Panel>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111111' }}>
            Variabele kosten per persoon — {periodLabel}
          </span>
          <InfoTip
            uitlijning="links"
            tekst="Elke transactie telt voor wie de pas op die dag had. Een pas die overgaat naar een andere collega verschuift de kosten dus vanaf dat moment mee, niet met terugwerkende kracht."
          />
        </div>
        <PeriodeKiezer months={months} period={period} onPeriod={onPeriod} />
      </div>

      {personen.length === 0 ? (
        <div style={{ fontSize: 13, color: '#888888' }}>Geen kosten in deze periode.</div>
      ) : (
        <MeerTonen items={personen} cap={CAP} meer={(n) => `+${n} meer tonen`}>
          {(zicht) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
              {zicht.map((r) => (
                <div
                  key={r.persoonId}
                  onClick={() => r.persoonId && onSelectPerson(r.persoonId)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '132px minmax(0, 1fr) 96px',
                    gap: 14,
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <PersoonLink
                    persoonId={r.persoonId}
                    naam={r.naam}
                    style={{
                      fontSize: 13,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  />
                  <div style={{ display: 'flex', height: 20, borderRadius: 3, overflow: 'hidden' }}>
                    {r.segments.map((s) => (
                      <div
                        key={s.key}
                        title={`${LABEL[s.key]}: ${euro(s.value)}`}
                        style={{
                          height: '100%',
                          background: COLOR[s.key],
                          width: `${(s.value / max) * 100}%`,
                          animation: 'vsGrowW .6s cubic-bezier(.4,0,.2,1) both',
                        }}
                      />
                    ))}
                  </div>
                  <span
                    style={{ fontSize: 13, textAlign: 'right', fontWeight: 700, color: '#111111' }}
                  >
                    {euro(r.bedrag)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </MeerTonen>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 18 }}>
        {lagen.map((b) => {
          const on = aan(b.key);
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => setUit((u) => ({ ...u, [b.key]: !u[b.key] }))}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                border: `1px solid ${on ? '#111111' : '#C9BBA8'}`,
                borderRadius: 2,
                background: on ? '#FFFFFF' : '#F4EEE4',
                cursor: 'pointer',
                fontFamily: 'Roboto, sans-serif',
                fontSize: 12,
                fontWeight: 700,
                color: on ? '#111111' : '#999999',
                opacity: on ? 1 : 0.7,
              }}
            >
              <span
                style={{
                  width: 11,
                  height: 11,
                  flex: 'none',
                  borderRadius: 2,
                  background: on ? b.color : 'transparent',
                  border: `2px solid ${b.color}`,
                }}
              />
              <span>{b.label}</span>
              <span style={{ color: '#888888', fontWeight: 400 }}>
                {euro(totalen.get(b.key) ?? 0)}
              </span>
            </button>
          );
        })}
      </div>
    </Panel>
  );
}
